import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { AdminHeader, Badge } from "@/pages/admin/AdminLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Plus } from "lucide-react";

const CATS = ["Active & Outdoors", "Food & Drink", "Games & Competition", "Arts & Entertainment", "Experiences", "Wellness & Mindfulness", "Nightlife & Social"];

export default function Activities() {
    const qc = useQueryClient();
    const [open, setOpen] = useState(false);
    const [edit, setEdit] = useState(null);
    const [form, setForm] = useState({ name: "", category: CATS[0], emoji: "🎯", active: true });
    const { data } = useQuery({ queryKey: ["admin-activities"], queryFn: async () => (await api.get("/admin/activities")).data });

    const openNew = () => { setEdit(null); setForm({ name: "", category: CATS[0], emoji: "🎯", active: true }); setOpen(true); };
    const openEdit = (a) => { setEdit(a); setForm({ name: a.name, category: a.category, emoji: a.emoji, active: a.active }); setOpen(true); };
    const save = async () => {
        try {
            if (edit) await api.put(`/admin/activities/${edit.id}`, form);
            else await api.post("/admin/activities", form);
            toast.success("Saved"); setOpen(false); qc.invalidateQueries({ queryKey: ["admin-activities"] });
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    };
    const toggle = async (a) => { await api.put(`/admin/activities/${a.id}`, { ...a, active: !a.active }); qc.invalidateQueries({ queryKey: ["admin-activities"] }); };

    const byCat = {};
    (data?.activities || []).forEach((a) => { (byCat[a.category] = byCat[a.category] || []).push(a); });

    return (
        <div>
            <div className="flex items-center justify-between">
                <AdminHeader title="Activities" subtitle="Add, edit, or disable activities members can choose." />
                <Button onClick={openNew} data-testid="activity-new"><Plus className="mr-1 h-4 w-4" /> New activity</Button>
            </div>
            <div className="space-y-6">
                {CATS.map((cat) => (
                    <div key={cat}>
                        <h3 className="mb-2 text-sm font-600 text-zinc-600">{cat}</h3>
                        <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
                            {(byCat[cat] || []).map((a) => (
                                <div key={a.id} data-testid={`activity-${a.id}`} className="flex items-center justify-between rounded-lg border border-zinc-200 bg-white px-4 py-3">
                                    <button onClick={() => openEdit(a)} className="text-left"><span className="mr-2">{a.emoji}</span>{a.name} {!a.active && <Badge>disabled</Badge>}</button>
                                    <Switch checked={a.active} onCheckedChange={() => toggle(a)} data-testid={`activity-toggle-${a.id}`} />
                                </div>
                            ))}
                        </div>
                    </div>
                ))}
            </div>
            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent>
                    <DialogHeader><DialogTitle>{edit ? "Edit activity" : "New activity"}</DialogTitle></DialogHeader>
                    <div className="space-y-3">
                        <div><label className="text-sm font-600">Name</label><Input data-testid="activity-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1" /></div>
                        <div className="grid grid-cols-2 gap-3">
                            <div><label className="text-sm font-600">Emoji</label><Input data-testid="activity-emoji" value={form.emoji} onChange={(e) => setForm({ ...form, emoji: e.target.value })} className="mt-1" /></div>
                            <div>
                                <label className="text-sm font-600">Category</label>
                                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                                    <SelectTrigger data-testid="activity-category" className="mt-1"><SelectValue /></SelectTrigger>
                                    <SelectContent>{CATS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                                </Select>
                            </div>
                        </div>
                    </div>
                    <DialogFooter><Button onClick={save} data-testid="activity-save">Save</Button></DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
